// app/javascript/controllers/ui/manifesto/magnetic_controller.js

import { Controller } from "@hotwired/stimulus"

/**
 * Magnetic Controller
 *
 * Элемент «притягивается» к курсору в радиусе strengthValue пикселей.
 * Smooth-follow через rAF и линейную интерполяцию.
 *
 * @usedby _hero.html.erb — magnetic CTA buttons
 *
 * Usage:
 *   <a data-controller="ui--manifesto--magnetic"
 *      data-ui--manifesto--magnetic-radius-value="120"
 *      data-ui--manifesto--magnetic-strength-value="0.35">
 *     Подключиться
 *   </a>
 *
 * Отключается на prefers-reduced-motion и на touch-устройствах.
 */
export default class extends Controller {
  static values = {
    radius:   { type: Number, default: 120 },
    strength: { type: Number, default: 0.35 }
  }

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    if (window.matchMedia("(hover: none)").matches) return

    this.target = { x: 0, y: 0 }
    this.current = { x: 0, y: 0 }
    this.running = false

    this.moveHandler = this.#onMove.bind(this)
    this.leaveHandler = this.#onLeave.bind(this)
    window.addEventListener("mousemove", this.moveHandler, { passive: true })
    this.element.addEventListener("mouseleave", this.leaveHandler)
  }

  disconnect() {
    if (this.moveHandler) window.removeEventListener("mousemove", this.moveHandler)
    if (this.leaveHandler) this.element.removeEventListener("mouseleave", this.leaveHandler)
    if (this.raf) cancelAnimationFrame(this.raf)
  }

  #onMove(e) {
    const rect = this.element.getBoundingClientRect()
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    const dx = e.clientX - cx
    const dy = e.clientY - cy
    const dist = Math.hypot(dx, dy)

    if (dist < this.radiusValue) {
      const factor = (1 - dist / this.radiusValue) * this.strengthValue
      this.target.x = dx * factor
      this.target.y = dy * factor
    } else {
      this.target.x = 0
      this.target.y = 0
    }

    if (!this.running) this.#loop()
  }

  #onLeave() {
    this.target.x = 0
    this.target.y = 0
  }

  #loop() {
    this.running = true
    this.current.x += (this.target.x - this.current.x) * 0.18
    this.current.y += (this.target.y - this.current.y) * 0.18

    this.element.style.transform = `translate3d(${this.current.x.toFixed(2)}px, ${this.current.y.toFixed(2)}px, 0)`

    if (Math.abs(this.current.x - this.target.x) > 0.1 || Math.abs(this.current.y - this.target.y) > 0.1) {
      this.raf = requestAnimationFrame(() => this.#loop())
    } else {
      this.running = false
    }
  }
}
